import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { normalizePhone, PHONE_REGEX, PHONE_HELP } from "@/lib/phone";
import { z } from "zod";
import {
  useListAccountContacts,
  useCreateAccountContact,
  useUpdateAccountContact,
  useDeleteAccountContact,
  getListAccountContactsQueryKey,
  type Contact,
  CreateContactBodyIdProofType,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { uploadItemPhoto } from "@/lib/upload";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash2, UserPlus, AlertTriangle, Camera, Loader2, X } from "lucide-react";
import { format } from "date-fns";

// Format a date-only string (YYYY-MM-DD) without a timezone shift.
function formatDateOnly(d: string | null | undefined): string | null {
  if (!d) return null;
  const parsed = new Date(`${d.slice(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : format(parsed, "dd MMM yyyy");
}

// Phase 23-07 CON-04: Contacts CRUD with Aadhaar warning banner
const ROLE_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "billing", label: "Billing" },
  { value: "user", label: "User" },
] as const;

const ID_PROOF_TYPES = Object.values(CreateContactBodyIdProofType);

const ContactFormSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  phone: z.string().regex(PHONE_REGEX, PHONE_HELP),
  email: z.string().email("Invalid email").nullable().optional().or(z.literal("")),
  roles: z.array(z.enum(["primary", "billing", "user"])).min(1, "Select at least one role"),
  idProofType: z.string().nullable().optional(),
  idProofRef: z.string().nullable().optional(),
  biometricId: z.string().nullable().optional(),
  upiVpa: z.string().nullable().optional(),
  photoUrl: z.string().nullable().optional(),
  dateOfBirth: z.string().nullable().optional(),
  joinedAt: z.string().nullable().optional(),
  exitedAt: z.string().nullable().optional(),
});

type ContactFormValues = z.infer<typeof ContactFormSchema>;

function initialsOf(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

// Single-photo uploader for a contact. Reuses /api/uploads/item-photo (public
// bucket, PNG/JPEG/WebP, compressed client-side) and stores the returned public
// URL in contacts.photo_url.
function ContactPhotoField({
  value,
  fullName,
  onChange,
}: {
  value: string | null | undefined;
  fullName: string;
  onChange: (next: string | null) => void;
}) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large.", description: "Photo must be 10MB or less.", variant: "destructive" });
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast({ title: "Unsupported file type.", description: "Use PNG, JPEG, or WebP.", variant: "destructive" });
      return;
    }
    setUploading(true);
    const result = await uploadItemPhoto(file);
    setUploading(false);
    if (!result.ok) {
      toast({ title: "Upload failed", description: result.message, variant: "destructive" });
      return;
    }
    onChange(result.url);
  }

  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-16 w-16">
        {value ? <AvatarImage src={value} alt={fullName || "Contact photo"} /> : null}
        <AvatarFallback className="text-sm">{initialsOf(fullName)}</AvatarFallback>
      </Avatar>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Uploading…</>
          ) : (
            <><Camera className="mr-2 h-3 w-3" />{value ? "Change photo" : "Upload photo"}</>
          )}
        </Button>
        {value && !uploading && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-destructive"
            onClick={() => onChange(null)}
          >
            <X className="mr-1 h-3 w-3" />Remove
          </Button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFileSelected}
        className="hidden"
      />
    </div>
  );
}

interface ContactDialogProps {
  open: boolean;
  accountId: number;
  contact?: Contact | null;
  onClose: () => void;
}

function ContactDialog({ open, accountId, contact, onClose }: ContactDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createContact = useCreateAccountContact();
  const updateContact = useUpdateAccountContact();

  // Build the form snapshot for a given contact (or a blank "Add" form).
  function valuesFor(c: Contact | null | undefined): ContactFormValues {
    return {
      fullName: c?.fullName ?? "",
      phone: normalizePhone(c?.phone ?? ""),
      email: c?.email ?? "",
      // New contacts default to "user" — the account already has a primary, and
      // checking Primary here will demote the existing one (single primary).
      roles: (c?.roles as ("primary" | "billing" | "user")[]) ?? ["user"],
      idProofType: c?.idProofType ?? null,
      idProofRef: c?.idProofRef ?? "",
      biometricId: c?.biometricId ?? "",
      upiVpa: c?.upiVpa ?? "",
      photoUrl: c?.photoUrl ?? "",
      dateOfBirth: c?.dateOfBirth ?? "",
      joinedAt: c?.joinedAt ?? new Date().toISOString().slice(0, 10),
      exitedAt: c?.exitedAt ?? "",
    };
  }

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(ContactFormSchema),
    defaultValues: valuesFor(contact),
  });

  // The dialog instance is mounted once and reused for both Add and Edit, so
  // useForm's defaultValues (read only on first render) go stale. Re-seed the
  // form from the target contact every time the dialog opens — this is what
  // makes Edit auto-populate the existing values.
  useEffect(() => {
    if (open) form.reset(valuesFor(contact));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id]);

  const watchedIdProofType = form.watch("idProofType");
  const isAadhaar = watchedIdProofType === "Aadhaar";
  const isPending = createContact.isPending || updateContact.isPending;

  async function onSubmit(values: ContactFormValues) {
    try {
      const body = {
        fullName: values.fullName,
        phone: values.phone,
        email: values.email || null,
        roles: values.roles as ("primary" | "billing" | "user")[],
        idProofType: (values.idProofType as typeof CreateContactBodyIdProofType[keyof typeof CreateContactBodyIdProofType] | null | undefined) ?? undefined,
        idProofRef: values.idProofRef || null,
        biometricId: values.biometricId || null,
        upiVpa: values.upiVpa || null,
        photoUrl: values.photoUrl || null,
        dateOfBirth: values.dateOfBirth || null,
        joinedAt: values.joinedAt || null,
        // End Date drives lifecycle status server-side (set → exited, cleared → active).
        exitedAt: values.exitedAt || null,
      };

      if (contact) {
        await updateContact.mutateAsync({ id: accountId, contactId: contact.id, data: body });
        toast({ title: "Contact updated." });
      } else {
        await createContact.mutateAsync({ id: accountId, data: body });
        toast({ title: "Contact added." });
      }
      queryClient.invalidateQueries({ queryKey: getListAccountContactsQueryKey(accountId) });
      onClose();
      form.reset();
    } catch (err) {
      const message = (err as Error)?.message ?? "";
      const code = (err as { data?: { code?: string } })?.data?.code ?? "";
      if (message.includes("409") || message.includes("primary_contact_exists")) {
        toast({
          title: "An account can have only one primary contact. Demote the existing primary first.",
          variant: "destructive",
        });
      } else if (code === "invalid_upi_vpa" || message.includes("invalid_upi_vpa")) {
        toast({
          title: "That UPI ID (VPA) doesn't look valid.",
          description: "Use the full form, e.g. name@bank — not a masked or partial ID.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
      }
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) { onClose(); form.reset(); } }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{contact ? "Edit Contact" : "Add Contact"}</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 mt-4">
            <FormField name="photoUrl" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Photo</FormLabel>
                <FormControl>
                  <ContactPhotoField
                    value={field.value}
                    fullName={form.watch("fullName")}
                    onChange={(next) => field.onChange(next ?? "")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField name="fullName" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name <span className="text-destructive">*</span></FormLabel>
                <FormControl><Input {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField name="phone" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Phone <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    placeholder="10-digit mobile (no +91, no spaces)"
                    maxLength={10}
                    {...field}
                    onChange={(e) => field.onChange(normalizePhone(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField name="email" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl><Input type="email" {...field} value={field.value ?? ""} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            {/* Roles — multi-checkbox */}
            <FormField
              name="roles"
              control={form.control}
              render={() => (
                <FormItem>
                  <FormLabel>Roles <span className="text-destructive">*</span></FormLabel>
                  <div className="flex gap-4">
                    {ROLE_OPTIONS.map(({ value, label }) => (
                      <FormField
                        key={value}
                        name="roles"
                        control={form.control}
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-1.5 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(value)}
                                onCheckedChange={(checked) => {
                                  const current = field.value as string[] ?? [];
                                  if (checked) {
                                    field.onChange([...current, value]);
                                  } else {
                                    field.onChange(current.filter((r) => r !== value));
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer">{label}</FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField name="idProofType" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>ID Proof Type</FormLabel>
                <Select
                  onValueChange={(v) => field.onChange(v === "none" ? null : v)}
                  value={field.value ?? "none"}
                >
                  <FormControl><SelectTrigger><SelectValue placeholder="Select ID type" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {ID_PROOF_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            {/* CON-04: Aadhaar warning banner — does NOT block save */}
            {isAadhaar && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Aadhaar storage warning</AlertTitle>
                <AlertDescription className="text-amber-700">
                  Storing Aadhaar requires a specific legal obligation. Add an obligation reference before proceeding.
                </AlertDescription>
              </Alert>
            )}

            {watchedIdProofType && watchedIdProofType !== "none" && (
              <FormField name="idProofRef" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    ID Reference
                    {isAadhaar && <span className="ml-1 text-xs text-muted-foreground">(last 4 digits only)</span>}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      placeholder={isAadhaar ? "Last 4 digits only" : "Reference number"}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            )}

            <FormField name="biometricId" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Biometric ID</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder="Enrollment ID for entry & conference room"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            {/* Phase 58 (POSPAY-01) — the member's own UPI ID, typed at onboarding.
                Its masked fingerprint is derived server-side and matched against
                Pine Labs POS payments; never entered directly here. */}
            <FormField name="upiVpa" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>UPI ID (VPA)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder="name@bank"
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  Used to match Pine Labs payments to this member.
                </p>
                <FormMessage />
              </FormItem>
            )} />

            <FormField name="dateOfBirth" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Date of Birth</FormLabel>
                <FormControl><Input type="date" {...field} value={field.value ?? ""} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField name="joinedAt" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>Joined Date</FormLabel>
                <FormControl><Input type="date" {...field} value={field.value ?? ""} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField name="exitedAt" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>End Date</FormLabel>
                <FormControl><Input type="date" {...field} value={field.value ?? ""} /></FormControl>
                <p className="text-xs text-muted-foreground">
                  Setting an end date marks the member as exited. Leave blank for an active member.
                </p>
                <FormMessage />
              </FormItem>
            )} />

            <SheetFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => { onClose(); form.reset(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving…" : contact ? "Save Changes" : "Add Contact"}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}

interface Props {
  accountId: number;
}

export function ContactsList({ accountId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: contacts, isLoading } = useListAccountContacts(accountId);
  const deleteContact = useDeleteAccountContact();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [deletingContactId, setDeletingContactId] = useState<number | null>(null);

  function openAdd() {
    setEditingContact(null);
    setDialogOpen(true);
  }

  function openEdit(contact: Contact) {
    setEditingContact(contact);
    setDialogOpen(true);
  }

  async function confirmDelete() {
    if (deletingContactId == null) return;
    try {
      await deleteContact.mutateAsync({ id: accountId, contactId: deletingContactId });
      toast({ title: "Contact removed." });
      queryClient.invalidateQueries({ queryKey: getListAccountContactsQueryKey(accountId) });
    } catch (err) {
      // The server returns 409 { code } on the structured ApiError.data; the
      // message is only "HTTP 409 Conflict", so read .data.code, not the message.
      const code =
        (err as { data?: { code?: string } })?.data?.code ?? (err as Error)?.message ?? "";
      toast({
        title: code.includes("cannot_delete_primary")
          ? "The primary contact can't be removed."
          : code.includes("contact_has_memberships")
            ? "This contact has active memberships — remove those first."
            : code.includes("contact_has_dependents")
              ? "This contact is linked to plan estimates / assignments — remove those first."
              : "Failed to remove contact.",
        variant: "destructive",
      });
    } finally {
      setDeletingContactId(null);
    }
  }

  return (
    <div className="space-y-4 pt-2">
      <div className="flex justify-end">
        <Button size="sm" onClick={openAdd}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add Contact
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Full Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(6)].map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : !contacts || contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-20 text-center text-muted-foreground">
                  No contacts yet. Add the first contact above.
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact: Contact) => {
                const isExited = contact.status === "exited";
                const exitedOn = formatDateOnly(contact.exitedAt);
                return (
                <TableRow key={contact.id} className={isExited ? "opacity-55" : undefined}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        {contact.photoUrl ? <AvatarImage src={contact.photoUrl} alt={contact.fullName} /> : null}
                        <AvatarFallback className="text-[10px]">{initialsOf(contact.fullName)}</AvatarFallback>
                      </Avatar>
                      <span>{contact.fullName}</span>
                    </div>
                  </TableCell>
                  <TableCell>{contact.phone}</TableCell>
                  <TableCell>{contact.email ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {contact.roles.map((r) => (
                        <Badge key={r} variant="outline" className="text-xs capitalize">{r}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={contact.status === "active" ? "default" : "secondary"} className="capitalize">
                      {contact.status}
                    </Badge>
                    {isExited && exitedOn && (
                      <div className="mt-1 text-xs text-muted-foreground">Ended {exitedOn}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(contact)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => setDeletingContactId(contact.id)}
                        disabled={contact.roles.includes("primary")}
                        title={
                          contact.roles.includes("primary")
                            ? "The primary contact can't be removed"
                            : "Remove contact"
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <ContactDialog
        open={dialogOpen}
        accountId={accountId}
        contact={editingContact}
        onClose={() => { setDialogOpen(false); setEditingContact(null); }}
      />

      <AlertDialog open={deletingContactId != null} onOpenChange={(v) => { if (!v) setDeletingContactId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove contact?</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive the contact record. The action can be reviewed in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
